import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Button, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    customers: [],
    totalCustomers: 0,
    totalUsers: 0,
    totalProjects: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  
  const { token } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get customers
        const customersResponse = await fetch('/api/admin/customers', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!customersResponse.ok) {
          throw new Error('Failed to fetch customers');
        }
        
        const customersData = await customersResponse.json();
        
        // Calculate total users and projects
        let totalUsers = 0;
        let totalProjects = 0;
        
        // Only process up to 5 customers for the dashboard to avoid too many requests
        const processLimit = Math.min(customersData.length, 5);
        const processedCustomers = [];
        
        for (let i = 0; i < processLimit; i++) {
          const customer = customersData[i];
          
          try {
            // Get users for this customer
            const usersResponse = await fetch(`/api/admin/${customer.CompanyName}/users`, {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (usersResponse.ok) {
              const usersData = await usersResponse.json();
              totalUsers += usersData.length;
              customer.userCount = usersData.length;
            } else {
              customer.userCount = 0;
            }
            
            // Get projects for this customer
            const projectsResponse = await fetch(`/api/admin/${customer.CompanyName}/projects`, {
              headers: {
                'x-auth-token': token
              }
            });
            
            if (projectsResponse.ok) {
              const projectsData = await projectsResponse.json();
              totalProjects += projectsData.length;
              customer.projectCount = projectsData.length;
            } else {
              customer.projectCount = 0;
            }
            
            processedCustomers.push(customer);
          } catch (err) {
            console.error(`Error processing customer ${customer.CompanyName}:`, err);
            // Still add the customer but with zero counts
            customer.userCount = 0;
            customer.projectCount = 0;
            processedCustomers.push(customer);
          }
        }
        
        setStats({
          customers: processedCustomers,
          totalCustomers: customersData.length,
          totalUsers,
          totalProjects
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, [token]);
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading dashboard...</div>;
  }
  
  return (
    <div>
      <h2 className="mb-4">Admin Dashboard</h2>
      
      <Row className="mb-4">
        <Col md={4}>
          <Card className="stats-card primary">
            <Card.Body>
              <Card.Title>Customers</Card.Title>
              <Card.Text className="display-4">{stats.totalCustomers}</Card.Text>
              <Button as={Link} to="/admin/customers" variant="outline-primary">View All Customers</Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="stats-card success">
            <Card.Body>
              <Card.Title>Total Users</Card.Title>
              <Card.Text className="display-4">{stats.totalUsers}</Card.Text>
              <Button as={Link} to="/admin/users" variant="outline-success">Manage Users</Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="stats-card warning">
            <Card.Body>
              <Card.Title>Total Projects</Card.Title>
              <Card.Text className="display-4">{stats.totalProjects}</Card.Text>
              <Button as={Link} to="/admin/customers" variant="outline-warning">View Customer Projects</Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Customer Overview</h5>
          <Button as={Link} to="/admin/customers/create" variant="primary" size="sm">
            Add Customer
          </Button>
        </Card.Header>
        <Card.Body>
          {stats.customers.length > 0 ? (
            <ListGroup>
              {stats.customers.map(customer => (
                <ListGroup.Item key={customer.CustomerId} className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6>{customer.CompanyName}</h6>
                    <small className="text-muted">
                      {customer.Email} | {customer.Phone || 'No phone'}
                    </small>
                  </div>
                  <div className="text-end">
                    <span className="badge bg-primary me-2">
                      {customer.userCount} Users
                    </span>
                    <span className="badge bg-success">
                      {customer.projectCount} Projects
                    </span>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="text-center">No customers found. Add your first customer.</p>
          )}
          
          {stats.totalCustomers > 5 && (
            <div className="text-center mt-3">
              <Button as={Link} to="/admin/customers" variant="outline-primary">
                View All {stats.totalCustomers} Customers
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminDashboard;
